import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Key, X } from "@phosphor-icons/react";

export default function ChangePasswordDialog({ open, onClose }) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPwd || !newPwd) {
      toast.error("Both current and new password are required");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Confirm password doesn't match");
      return;
    }
    if (newPwd === currentPwd) {
      toast.error("New password must be different from current");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Password updated. Use it on next login.");
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onClick={() => { reset(); onClose(); }}
      data-testid="change-password-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key size={18} weight="bold" />
            <h2 className="font-display text-lg font-bold">Change Password</h2>
          </div>
          <button
            type="button"
            onClick={() => { reset(); onClose(); }}
            className="text-ink-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              data-testid="cp-current"
              autoComplete="current-password"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              placeholder="Your current password"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              New Password (≥ 6 chars)
            </label>
            <input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="cp-new"
              autoComplete="new-password"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              data-testid="cp-confirm"
              autoComplete="new-password"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              placeholder="Re-type new password"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-canvas-line">
          <button
            type="button"
            onClick={() => { reset(); onClose(); }}
            className="px-3 py-2 border border-canvas-line text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="cp-submit"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Updating…" : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
